<template>
    <div class="mr-px text-white border-l border-gray-800 border-dashed divide-gray-800 md:w-48 lg:w-64">
        <TransitionRoot
            :show="sidebarOpen"
            as="template"
        >
            <Dialog
                as="div"
                class="fixed inset-0 z-30 flex md:hidden"
                @close="sidebarOpen = false"
            >
                <TransitionChild
                    as="template"
                    enter="transition-opacity ease-linear duration-300"
                    enter-from="opacity-0"
                    enter-to="opacity-100"
                    leave="transition-opacity ease-linear duration-300"
                    leave-from="opacity-100"
                    leave-to="opacity-0"
                >
                    <DialogOverlay class="fixed inset-0 bg-black bg-opacity-75" />
                </TransitionChild>
                <TransitionChild
                    as="template"
                    enter="transition ease-in-out duration-300 transform"
                    enter-from="-translate-x-full"
                    enter-to="translate-x-0"
                    leave="transition ease-in-out duration-300 transform"
                    leave-from="translate-x-0"
                    leave-to="-translate-x-full"
                >
                    <div class="relative flex flex-col flex-1 w-full max-w-xs bg-gray-900">
                        <TransitionChild
                            as="template"
                            enter="ease-in-out duration-300"
                            enter-from="opacity-0"
                            enter-to="opacity-100"
                            leave="ease-in-out duration-300"
                            leave-from="opacity-100"
                            leave-to="opacity-0"
                        >
                            <div class="absolute top-0 right-0 pt-2 -mr-12">
                                <button
                                    class="flex items-center justify-center w-10 h-10 ml-1 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                                    type="button"
                                    @click="sidebarOpen = false"
                                >
                                    <span class="sr-only">Close sidebar</span>
                                    <XIcon
                                        aria-hidden="true"
                                        class="w-6 h-6 text-white"
                                    />
                                </button>
                            </div>
                        </TransitionChild>
                        <div class="flex-1 overflow-y-auto">
                            <nav class="flex-1 pt-5 pb-48 ml-5 space-y-1 text-white border-l border-gray-600 border-dashed">
                                <VersionButton />
                                <div
                                    v-for="item in menu"
                                    :key="item.name"
                                    :class="item.style"
                                    class="w-full px-6 py-2 text-sm font-medium text-left"
                                >
                                    <component
                                        :is="item.type"
                                        :href="item.href"
                                        :target="item.target"
                                        :to="item.href"
                                        class="flex items-center space-x-2"
                                    >
                                        <component
                                            :is="item.icon"
                                            aria-hidden="true"
                                            class="w-5 h-5"
                                        />
                                        <p
                                            :class="$route.path.includes(item.href) ? 'text-purple-400' : 'hover:text-gray-400'"
                                        >
                                            {{ item.name }}
                                        </p>
                                    </component>
                                </div>
                                <div
                                    v-for="(data, index) in sections"
                                    :key="index"
                                >
                                    <hr class="border-gray-600">
                                    <div
                                        class="w-full px-6 py-2 text-sm font-medium text-left"
                                    >
                                        <p>
                                            {{ data.title }}
                                        </p>
                                    </div>
                                    <hr class="border-gray-600 border-dashed">
                                    <div
                                        v-for="submenu in data.submenu"
                                        :key="submenu.name"
                                        :class="submenu.style"
                                        class="w-full py-2 text-sm font-medium text-left submenu"
                                    >
                                        <router-link
                                            :class="[submenu.current ? 'text-purple-400 border-l border-purple-600' : 'text-gray-400 hover:text-white', 'group flex items-center px-6 text-xs font-medium']"
                                            :to="submenu.routerLink"
                                            class="flex items-center"
                                        >
                                            <p>
                                                {{ submenu.name }}
                                            </p>
                                        </router-link>
                                    </div>
                                </div>
                            </nav>
                        </div>
                    </div>
                </TransitionChild>
                <div class="flex-shrink-0 w-14">
                    <!-- Force sidebar to shrink to fit close icon -->
                </div>
            </Dialog>
        </TransitionRoot>

        <!-- Static sidebar for desktop -->
        <div
            class="hidden md:flex md:w-48 lg:w-64 md:top-12 md:sticky md:overflow-y-auto docnav overscroll-y-contain"
            style="max-height: calc(100vh - 3rem)"
        >
            <!-- Sidebar component, swap this element with another sidebar if you like -->
            <div class="flex flex-col flex-1 min-h-0 bg-gray-900">
                <div class="flex flex-col flex-1 pb-48">
                    <!-- Sections -->
                    <nav class="flex-1">
                        <VersionButton />
                        <div
                            v-for="item in menu"
                            :key="item.name"
                            :class="[$route.path.includes(item.href) ? 'text-purple-400' : '']"
                            class="w-full text-xs font-medium text-left text-gray-400 hover:text-white"
                        >
                            <component
                                :is="item.type"
                                :href="item.href"
                                :target="item.target"
                                :to="item.href"
                                class="flex items-center px-6 my-4"
                            >
                                <component
                                    :is="item.icon"
                                    aria-hidden="true"
                                    class="w-5 h-5 mr-4"
                                />
                                <p>
                                    {{ item.name }}
                                </p>
                            </component>
                        </div>
                        <div
                            v-for="(data, index) in sections"
                            :key="index"
                        >
                            <hr class="border-gray-600">
                            <div
                                class="w-full px-6 py-2 text-sm font-medium text-left"
                            >
                                <p>
                                    {{ data.title }}
                                </p>
                            </div>
                            <hr class="border-gray-800 border-dashed">
                            <div class="pt-0 pb-4">
                                <div
                                    v-for="submenu in data.submenu"
                                    :key="submenu.name"
                                    :class="submenu.style"
                                    class="w-full py-2 text-sm font-medium text-left submenu"
                                >
                                    <router-link
                                        :class="[submenu.current ? 'text-purple-400 border-l border-purple-600' : 'text-gray-400 hover:text-white', 'group flex items-center px-6 text-xs font-medium']"
                                        :to="submenu.routerLink"
                                        class="flex items-center"
                                    >
                                        <p>
                                            {{ submenu.name }}
                                        </p>
                                    </router-link>
                                </div>
                            </div>
                        </div>
                    </nav>
                </div>
            </div>
        </div>
        <div class="flex flex-col flex-1 md:pl-64">
            <div class="pt-1 pl-1 bg-gray-900 border-b border-gray-700 md:hidden sm:pl-3 sm:pt-3">
                <button
                    class="-ml-0.5 mt-2 h-10 w-10 inline-flex text-center rounded-md text-gray-500 hover:text-gray-100 focus:outline-none"
                    type="button"
                    @click="sidebarOpen = true"
                >
                    <span class="sr-only">Open sidebar</span>
                    <ChevronRightIcon
                        aria-hidden="true"
                        class="justify-center w-6 h-6"
                    />
                </button>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import VersionButton from "./VersionButton.vue";
import { ref } from "vue";
import { Dialog, DialogOverlay, TransitionChild, TransitionRoot } from "@headlessui/vue";
import { ChevronRightIcon, XIcon } from "@heroicons/vue/solid";

const sidebarOpen = ref(false);

defineProps({
    menu: {
        type: Array,
        default: null
    },
    sections: {
        type: Array,
        default: null
    }
});

</script>

<style scoped>
.submenu a.router-link-active {
    @apply text-purple-400 border-l border-purple-500
}

/* Works on Firefox */
.docnav {
    scrollbar-width: thin;
    scrollbar-color: rgb(212, 212, 216) rgb(24, 24, 27);
}

/* Works on Chrome, Edge, and Safari */
.docnav::-webkit-scrollbar {
    @apply w-0;
}

.docnav:hover::-webkit-scrollbar {
    @apply w-1;
}

.docnav::-webkit-scrollbar-track {
    @apply bg-transparent
}

.docnav::-webkit-scrollbar-thumb {
    @apply bg-transparent;
}

.docnav:hover::-webkit-scrollbar-thumb {
    @apply bg-gray-300 rounded-full;
}

</style>
